import type {Table as ReactTable} from '@tanstack/react-table';
import {Columns3, FileX} from 'lucide-react';
import {useTranslation} from 'react-i18next';

import TableActions from '@/components/data-table/TableActions';
import TableNotFound from '@/components/data-table/TableNotFound';
import TablePagination from '@/components/data-table/TablePagination';
import TableSearch from '@/components/data-table/TableSearch';
import TableView from '@/components/data-table/TableView';
import InfoState from '@/components/InfoState';
import {Button} from '@/components/ui/button';
import {PageSkeleton} from '@/components/ui/skeleton';
import {Typography} from '@/components/ui/typography';

export type ExportPreviewProps = {
  table: ReactTable<unknown>;
  isLoading: boolean;
  exportCount: number;
};

const ExportPreview = ({table, isLoading, exportCount}: ExportPreviewProps) => {
  const {t} = useTranslation();

  if (isLoading) return <PageSkeleton parts={['toolbar']} view="list" />;

  if (!table.getCoreRowModel().rows.length) {
    return (
      <InfoState
        icon={<FileX size={40} className="text-mint-500" />}
        title={t('There is no data to export')}
      />
    );
  }

  const hasColumns = table
    .getVisibleLeafColumns()
    .some(column => column.getCanHide());

  return (
    <div className="flex flex-col gap-4">
      <TableSearch table={table} placeholder={t('Search')} />
      <TableActions
        table={table}
        showSort={false}
        showVisibility
        endChildren={
          !table.getIsAllColumnsVisible() && (
            <Button
              type="button"
              variant="link"
              size="small"
              onClick={() => table.toggleAllColumnsVisible(true)}
            >
              {t('Select all fields')}
            </Button>
          )
        }
      />
      {hasColumns ? (
        <>
          <Typography className="text-sm text-neutral-dark">
            {t('Select rows to export only them, or leave all unselected.')}
          </Typography>
          <TableView
            table={table}
            tableClassName="text-xs"
            showSortingControls={false}
          />
          <TableNotFound table={table} />
          <TablePagination table={table} />
          <Typography className="self-end text-sm">
            {t('Records to export')}: <strong>{exportCount}</strong>
          </Typography>
        </>
      ) : (
        <InfoState
          icon={<Columns3 size={40} className="text-mint-500" />}
          title={t('No columns selected')}
          description={t('Choose at least one column to export.')}
        />
      )}
    </div>
  );
};

export default ExportPreview;
